import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FileUrlResponse,
  FieldValue,
  getFileUrl,
  getSuggestedFieldValues,
  updateInteraction,
  getInteractionMetadata,
  fetchChatTranscript,
  fetchEmailTranscript,
  UpdateInteractionTagsRequest,
  updateInteractionTags,
  getInteractionTags,
  UpdateInteractionViewsRequest,
  updateInteractionViews,
  bulkUpdateInteractions,
} from "../api/interaction";
import {
  DownloadInteractionFileRequest,
  downloadInteractionFile,
} from "../helpers/fileDownload";
import { Email } from "../types/email/email";
import { BulkUpdateInteractionsRequest } from "../types/interaction/BulkUpdate";

/* istanbul ignore next */
export const useFileUrl = (
  interactionId?: string,
  customerId?: string,
  ready: boolean = true
) => {
  return useQuery<FileUrlResponse | undefined, Error>({
    queryKey: ["fileUrl", interactionId, customerId, ready],
    queryFn: () => getFileUrl(interactionId!, customerId),
    enabled: !!interactionId && !!customerId && ready,
  });
};

/* istanbul ignore next */
export const useChatTranscript = (
  customerId: string | undefined,
  interactionId: string | undefined
) => {
  return useQuery({
    queryKey: ["chatTranscript", customerId, interactionId],
    queryFn: () => fetchChatTranscript(customerId!, interactionId!),
    enabled: !!customerId && !!interactionId,
  });
};

/* istanbul ignore next */
export const useEmailTranscript = (fileUrl?: string) => {
  return useQuery<Email, Error>({
    queryKey: ["emailTranscript", fileUrl],
    queryFn: () => fetchEmailTranscript(fileUrl!),
    enabled: !!fileUrl,
  });
};

export const useInteractionFileDownload = () => {
  return useMutation<void, Error, DownloadInteractionFileRequest>({
    mutationKey: ["downloadInteractionFile"],
    mutationFn: downloadInteractionFile,
  });
};

export const useInteractionTags = (customerId?: string) => {
  return useQuery<string[], Error>({
    queryKey: ["interactionTags", customerId],
    queryFn: () => getInteractionTags(customerId),
    enabled: !!customerId,
  });
};

/* istanbul ignore next */
export const useTypeaheadSearch = (
  fieldName?: string,
  pascalFieldName?: string,
  value?: string,
  customerId?: string
) => {
  return useQuery<FieldValue[], Error>({
    queryKey: [
      "getSuggestedFieldValues",
      fieldName,
      pascalFieldName,
      value,
      customerId,
    ],
    queryFn: () =>
      getSuggestedFieldValues({
        customerId,
        fieldName: fieldName!,
        pascalFieldName: pascalFieldName!,
        value: value!,
      }),
    enabled:
      !!customerId &&
      !!fieldName &&
      !!pascalFieldName &&
      !!value &&
      value.length >= 3,
    retryDelay: 1000,
  });
};

/* istanbul ignore next */
export const useUpdateInteraction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateInteraction"],
    mutationFn: updateInteraction,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["interactions", variables.customerId],
      });
    },
  });
};

/* istanbul ignore next */
export const useUpdateInteractionTags = () => {
  return useMutation({
    mutationKey: ["updateInteractionTags"],
    mutationFn: (request: UpdateInteractionTagsRequest) =>
      updateInteractionTags(request),
  });
};

/* istanbul ignore next */
export const useUpdateInteractionViews = () => {
  return useMutation({
    mutationKey: ["updateInteractionViews"],
    mutationFn: (request: UpdateInteractionViewsRequest) =>
      updateInteractionViews(request),
  });
};

/* istanbul ignore next */
export const useInteractionMetadata = (
  customerId: string,
  interactionId: string
) => {
  return useQuery<Record<string, unknown>, Error>({
    queryKey: ["interactionMetadata", customerId, interactionId],
    queryFn: () => getInteractionMetadata({ customerId, interactionId }),
    enabled: !!customerId && !!interactionId,
  });
};

export const useBulkUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: BulkUpdateInteractionsRequest) =>
      bulkUpdateInteractions(req),
    onSuccess: (_data, variables) => {
      // refetch interactions
      queryClient.invalidateQueries({
        queryKey: ["interactions", variables.customerId],
      });
    },
  });
};
